'use client';

import { useEffect, useRef, useState } from 'react';
import { useEvento } from '@/store/evento/evento';
import { useEventoContext } from '../../contexts';
import { checkout } from '@/services/payway.service';
import { setCompraAccessToken } from '@/helpers/compraAccessToken';
import { reserveLifecycle } from '../../hooks/reserveLifecycle';

type Props = {
    amount: number;
    onError?: (err: any) => void;
};

export function MetodoPagoAllaria({ amount, onError }: Props) {
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const calledRef = useRef(false);

    /*
       aliveRef refleja si el componente sigue montado. Usamos un ref (no una
       variable de closure) para que se resetee a true en cada entry del
       useEffect — necesario para React 18 + StrictMode, que corre el effect
       dos veces (mount → cleanup → re-mount) y nos dejaba el flag en false
       después de la primera cleanup, abortando el redirect aunque el componente
       seguía vivo.
    */
    const aliveRef = useRef(true);

    const { evento } = useEventoContext();
    const { state: funcion } = useEvento.use.funcion();
    const { state: entradas } = useEvento.use.entradas();
    const { state: comprador } = useEvento.use.comprador();
    const { state: reservaId } = useEvento.use.reserva();

    useEffect(() => {
        aliveRef.current = true;

        // En la segunda corrida de StrictMode, calledRef ya es true: no
        // arrancamos otro checkout pero igual seteamos el cleanup para cuando
        // el componente realmente se desmonte.
        if (calledRef.current) {
            return () => {
                aliveRef.current = false;
            };
        }
        calledRef.current = true;

        /*
           Guard global: si otro mount ya está procesando un checkout Allaria
           (user tocó Allaria → back → Allaria rápido), no arrancar otro.
        */
        if (reserveLifecycle.isCheckoutInFlight()) {
            return () => {
                aliveRef.current = false;
            };
        }
        reserveLifecycle.markCheckoutInFlight();

        const initCheckout = async () => {
            try {
                const resp = await checkout({
                    funcionId: funcion!.id,
                    eventoId: evento.id,
                    entradas,
                    comprador: comprador!,
                    gateway: 'allaria',
                    reservaId: reservaId!,
                    paymentData: {
                        transaction_amount: amount,
                        payer: { email: comprador?.email },
                    },
                });

                if (!aliveRef.current) return;

                console.log('[ALLARIA CHECKOUT] response', resp);

                if (!('mode' in resp) || resp.mode !== 'redirect' || !resp.checkoutUrl) {
                    throw new Error('Respuesta inesperada del servidor');
                }

                if (resp.accessToken) {
                    setCompraAccessToken(resp.compraId, resp.accessToken);
                }

                /*
                   localStorage con key por paymentId: dos compras paralelas
                   no se pisan. El return page lee `?purchaseId=` de la URL
                   (que manda Allaria en el redirect) para encontrar su entry.
                */
                localStorage.setItem(`allaria_pending_order:${resp.paymentId}`, JSON.stringify({
                    compraId: resp.compraId,
                    accessToken: resp.accessToken,
                }));

                reserveLifecycle.set('skip-unload');
                window.location.href = resp.checkoutUrl;
            } catch (err: any) {
                console.error('[ALLARIA CHECKOUT] error', err);
                if (!aliveRef.current) return;
                const msg = err?.message ?? 'No pudimos iniciar el pago con Allaria+';
                setErrorMsg(msg);
                onError?.(err);
            } finally {
                if (aliveRef.current) {
                    reserveLifecycle.clearCheckoutInFlight();
                }
            }
        };

        initCheckout();

        return () => {
            /*
               Cleanup al desmontar. En StrictMode esto corre entre los dos
               mounts y aliveRef se vuelve a poner true al inicio del próximo
               effect. En un unmount real (user navegó back antes del redirect),
               aliveRef queda en false definitivamente, el await bail-out
               funciona y los flags globales se limpian.
            */
            aliveRef.current = false;
            reserveLifecycle.clearCheckoutInFlight();
            if (reserveLifecycle.is('skip-unload')) {
                reserveLifecycle.set('idle');
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (errorMsg) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4 text-white">
                <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <p className="text-red-300 text-sm text-center">{errorMsg}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4 text-white">
            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            <p className="text-sm text-white/70">Te estamos redirigiendo al checkout de Allaria+...</p>
        </div>
    );
}
